import { pageLocation } from "@/services/pageLocation";
import { useEffect, useState, useRef, useReducer, Fragment } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import PreviewCbb from "../components/PreviewCbb";
import { getCookie } from "../services/cookieService";
import environment from "../services/environment";
import ReadPdfModal from "../components/ReadPdfModal";
import { axiosGet, axiosPost } from "@/services/axiosService";
import getTextColor from "@/utils/getTextColor";
import RecentMessages from "./RecentMessages";
import { defaultConversations } from "@/language.constants";
import { BotIconOld, OutgrowBotIcon, WhiteBotIcon } from "@/components/Icons";
import { useParseGradient } from "@/hooks/useParseGradient";
import { useRTLDirection } from "@/hooks/useRTLDirection";
import Error404, { ErrorType } from "./Error404";
import EntryBranding from "@/components/EntryBranding";
import { sanitizeBrandingCss } from "@/utils/sanitizeBrandingCss";
import deriveShade from "@/utils/deriveShade";
import { useParentWindowWidth } from "@/hooks/useParentWindowWidth";
import OutgrowError404 from "./OutgrowError404";
import { NoConverstionSvg } from "./NoConverstionSvg.tsx";
import { COMPARE_MODE } from "@/utils/compareMode";
import { applyImageFallback } from "@/utils/brandingImageFallback";

const PAGE_CONTEXT_KEYS = [
  "parent_url",
  "page_title",
  "page_heading",
  "page_meta_title",
  "page_description",
  "page_url_slug",
];
const PAGE_STARTER_SESSION_STORAGE_KEY = "cbb-page-aware-starter-content:v2";
/** Longest the bot's first load waits for page-aware content before showing the saved content. */
const STARTER_REQUEST_TIMEOUT_MS = 3000;
/** When AI content is still being written, ask again once after this long. */
const STARTER_PENDING_RETRY_MS = 5000;
const MAX_PAGE_STARTER_SESSION_ENTRIES = 25;
/** Responses that are final for this page. Anything else (pending, error, …) is not stored. */
const CACHEABLE_STARTER_STATUSES = ["static", "hit", "miss"];

type PageStarterContent = {
  initialMessage?: string;
  questions?: any[];
};

type PageStarterSessionEntry = {
  content: PageStarterContent;
  shownAt: string;
};

// The query string is kept (only the #hash is dropped), so /product?id=1 and
// /product?id=2 in the same tab don't share starter content.
function normalizePageUrl(value: string) {
  try {
    const url = new URL(value);
    url.hash = "";
    return url.toString();
  } catch {
    return value;
  }
}

function getPageStarterSessionEntries(): Record<
  string,
  PageStarterSessionEntry
> {
  try {
    const rawEntries = window.sessionStorage.getItem(
      PAGE_STARTER_SESSION_STORAGE_KEY,
    );
    return rawEntries ? JSON.parse(rawEntries) : {};
  } catch {
    return {};
  }
}

function savePageStarterSessionEntry(key: string, content: PageStarterContent) {
  try {
    const entries = getPageStarterSessionEntries();
    entries[key] = { content, shownAt: new Date().toISOString() };
    // Keep only the most recent pages: session storage is small and shared with the site.
    const recentEntries = Object.entries(entries)
      .sort(([, a], [, b]) =>
        String(b?.shownAt).localeCompare(String(a?.shownAt)),
      )
      .slice(0, MAX_PAGE_STARTER_SESSION_ENTRIES);
    window.sessionStorage.setItem(
      PAGE_STARTER_SESSION_STORAGE_KEY,
      JSON.stringify(Object.fromEntries(recentEntries)),
    );
  } catch {
    // Starter content still works when browser storage is unavailable.
  }
}

export default function EntryCbb({ outgrow = false }: { outgrow?: boolean }) {
  const { url } = useParams();
  const environmentData = environment();
  const { parseGradient } = useParseGradient();
  const { parentWidth, isMobileParent } = useParentWindowWidth();
  const subDomain = pageLocation().hostname.split(".")[0];
  const [searchParams, _] = useSearchParams();
  const isEmbeddedPreview =
    searchParams.get("embed") === "true" && searchParams.has("parent_url");
  const embed = useRef(isEmbeddedPreview);
  const [botData, setBotData] = useState<Record<string, any>>({});
  const [chatConfig, setChatConfig] = useState(botData?.chatConfig);
  const rtl = useRTLDirection(chatConfig);
  const [loader, setLoader] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [readPdf, setReadPdf] = useState(false);
  const [readPdfText, setReadPdfText] = useState({ title: "", content: "" });
  const [consent, setConsent] = useState<boolean>(checkExistingConsent());
  const [locationData, setLocationData] = useState<Record<string, any>>({
    ip: "",
    location: "unknown",
    country: "",
    region: "",
    city: "",
  });
  const isPreviewMode = pageLocation().pathname.includes("/preview/");
  const [recentConversations, setRecentConversations] = useState<any[]>([]);
  const [recentConversationsLoader, setRecentConversationsLoder] =
    useState(false);
  const [contactName, setContactName] = useState("");
  const [resumeConversationData, setResumeConversationData] = useState<Record<
    string,
    any
  > | null>(null);
  const embedData = useRef<Record<string, any>>({});
  const [contactData, setContactData] = useState<Record<string, string> | null>(
    null,
  );
  const [previewReloadKey, reloadPreview] = useReducer((pre) => pre + 1, 0);
  const [roomId, setRoomId] = useState(null);
  const [creditId, setCreditId] = useState(null);
  const [isTrial, setIsTrial] = useState(false);
  const [fromOutgrow, setFromOutgrow] = useState(false);
  const previewComponentRef = useRef<any>();
  const resolvingStarterContent = useRef(false);
  const resolvedPageContextKey = useRef("");
  // The bot as loaded, before any page-aware content. Every page starts from it,
  // so a failed request never leaves the previous page's content on screen.
  const baseBotData = useRef<Record<string, any> | null>(null);
  const [starterRetry, setStarterRetry] = useState<{
    key: string;
    attempt: number;
  } | null>(null);
  const refreshStarterContentRef = useRef<
    (payload: Record<string, any>, options?: { retry?: boolean }) => void
  >(() => {});
  refreshStarterContentRef.current = refreshPageAwareStarterContent;

  // AI starters for this page were still being written: try once more, on the same page only.
  useEffect(() => {
    if (!starterRetry || starterRetry.attempt > 1) return;
    const timer = window.setTimeout(() => {
      const sourceBotData = baseBotData.current;
      if (!sourceBotData) return;
      const pageContext = getPageContext(embedData.current);
      if (
        getPageStarterSessionKey(sourceBotData, pageContext) !==
        starterRetry.key
      )
        return;
      refreshStarterContentRef.current({}, { retry: true });
    }, STARTER_PENDING_RETRY_MS);
    return () => window.clearTimeout(timer);
  }, [starterRetry]);
  const [errorType, setErrorType] = useState<ErrorType | null>(null);

  useEffect(() => {
    init();
  }, []);

  function notifyParentPreviewTab(tab: "welcome" | "conversation") {
    const message = {
      type: "LIVEBOT_PREVIEW_TAB_CHANGE",
      tab,
    };

    window.parent.postMessage(message, "*");
    if (
      isPreviewMode &&
      isEmbeddedPreview &&
      window.top &&
      window.top !== window.parent
    ) {
      window.top.postMessage(message, "*");
    }
  }

  async function applyCustomCss(customCSS: string, styleId = "cbb-custom-css") {
    if (typeof document === "undefined") return;

    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = customCSS ?? "";
    document.head.appendChild(style);

    setTimeout(() => sanitizeBrandingCss(styleId), 30);
  }

  function removeCustomCss(styleId: string) {
    if (typeof document === "undefined") return;
    document.getElementById(styleId)?.remove();
  }

  async function init() {
    try {
      processEmbedData();
      fetchLocation().catch((err) =>
        console.log("Failed to fetch location: ", err),
      );

      const fetchedBotData = await fetchBotData();
      baseBotData.current = fetchedBotData;
      const fromOutgrowValue = await fetchCredits(fetchedBotData.companyId);
      const isOutgrowContext = outgrow || fromOutgrowValue;

      if (embed.current) {
        if (typeof document === "undefined") return;
        const style = document.createElement("style");
        style.id = "cbb-custom-css-inline";
        style.innerHTML = `${fetchedBotData.chatConfig.customCSS} ${fetchedBotData.chatConfig.customCSSConversation}`;
        document.head.appendChild(style);

        const contact = await fetchContact(fetchedBotData.companyId);
        const recentConversation = await fetchRecentConversations(
          contact,
          fetchedBotData._id,
        );

        setShowPreview(!recentConversation.length);

        const runtimeBotData = recentConversation.length
          ? fetchedBotData
          : await resolvePageAwareStarterContent(fetchedBotData);
        setBotData(runtimeBotData);
        setChatConfig(runtimeBotData.chatConfig);
        initEmbed(runtimeBotData, isOutgrowContext);
      } else {
        removeCustomCss("cbb-custom-css-inline");
        removeCustomCss("cbb-custom-css");
        removeCustomCss("cbb-custom-css-conversation");
        setShowPreview(true);
      }
    } catch (err: any) {
      console.log(err);
      if (
        !navigator.onLine ||
        err?.code === "ERR_NETWORK" ||
        err?.message === "Network Error"
      ) {
        setErrorType("low-internet");
      } else if (err?.response?.data?.message === "No bot found") {
        setErrorType("not-found");
      } else {
        setErrorType("something-wrong");
      }
    } finally {
      setLoader(false);
    }
  }

  useEffect(() => {
    window.addEventListener("message", handleEmbedEvents);

    return () => {
      window.removeEventListener("message", handleEmbedEvents);
    };
  }, [
    botData,
    contactData,
    recentConversations,
    roomId,
    resumeConversationData,
  ]);

  function applyFontLink(fontURL?: string, id = "dynamic-font") {
    if (!fontURL || typeof document === "undefined") {
      return;
    }

    const existingFont = document.getElementById(id) as HTMLLinkElement | null;

    if (existingFont) {
      existingFont.href = fontURL;
      return;
    }

    const font = document.createElement("link");
    font.rel = "stylesheet";
    font.id = id;
    font.href = fontURL;
    document.head.appendChild(font);
  }

  useEffect(() => {
    applyFontLink(
      chatConfig?.greetingConfig?.typography?.font?.fontURL,
      "entry-dynamic-font",
    );
  }, [chatConfig?.greetingConfig?.typography?.font?.fontURL]);

  useEffect(() => {
    applyFontLink(chatConfig?.typography?.font?.fontURL, "dynamic-font");
  }, [chatConfig?.typography?.font?.fontURL]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "BOT_DATA_UPDATE") {
        const nextBotData = event.data.botData;
        const nextChatConfig = event.data.chatConfig ?? nextBotData?.chatConfig;

        if (nextBotData) {
          baseBotData.current = nextBotData;
          setBotData(nextBotData);
        }

        if (nextChatConfig) {
          setChatConfig(nextChatConfig);
          if (embed.current) {
            applyCustomCss(nextChatConfig.customCSS, "cbb-custom-css");
            applyCustomCss(
              nextChatConfig.customCSSConversation,
              "cbb-custom-css-conversation",
            );
          } else {
            removeCustomCss("cbb-custom-css");
            removeCustomCss("cbb-custom-css-conversation");
          }
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  if (errorType) {
    return outgrow && !embed.current ? (
      <OutgrowError404 />
    ) : (
      <Error404 errorType={errorType} />
    );
  }

  async function fetchBotData() {
    if (!url) throw new Error("bot url missing");
    const params = new URLSearchParams({ url });

    if (isPreviewMode) {
      params.set("status", "dev");
    } else {
      params.set("status", "live");
    }

    if (environmentData.ENVIRONMENT == "production") {
      params.set("subDomain", subDomain);
    }

    if (outgrow) {
      params.set("outgrow", "true");
    }

    const res = await axiosGet("/users/questions", { params });
    setBotData(res.data.botData);
    setChatConfig(res.data.botData.chatConfig);
    setChatConfig(res.data.botData.chatConfig);

    return res.data.botData;
  }

  function getPageContext(data = embedData.current) {
    return PAGE_CONTEXT_KEYS.reduce(
      (context, key) => {
        const value = data?.[key];
        if (typeof value === "string" && value.trim()) {
          context[key] = value.trim();
        }
        return context;
      },
      {} as Record<string, string>,
    );
  }

  function hasPageContextUpdate(payload: Record<string, unknown>) {
    return PAGE_CONTEXT_KEYS.some((key) => typeof payload?.[key] === "string");
  }

  function getPageStarterSessionKey(
    sourceBotData: Record<string, any>,
    pageContext: Record<string, string>,
  ) {
    return [
      sourceBotData._id,
      sourceBotData.updatedAt || "",
      normalizePageUrl(pageContext.parent_url || ""),
    ].join(":");
  }

  function applyPageStarterContent(
    sourceBotData: Record<string, any>,
    content: PageStarterContent,
  ): Record<string, any> {
    return {
      ...sourceBotData,
      aiStarterQues: {
        ...sourceBotData.aiStarterQues,
        initialMsg: content.initialMessage?.trim()
          ? content.initialMessage
          : sourceBotData.aiStarterQues.initialMsg,
        data:
          Array.isArray(content.questions) && content.questions.length
            ? content.questions
            : sourceBotData.aiStarterQues.data,
      },
    };
  }

  async function resolvePageAwareStarterContent(
    sourceBotData: Record<string, any>,
    sourceEmbedData = embedData.current,
  ): Promise<Record<string, any>> {
    if (
      !embed.current ||
      !sourceBotData?.aiStarterQues?.webEmbed?.enabled ||
      !sourceBotData?._id
    ) {
      return sourceBotData;
    }

    const pageContext = getPageContext(sourceEmbedData);
    if (!pageContext.parent_url) return sourceBotData;
    const pageStarterSessionKey = getPageStarterSessionKey(
      sourceBotData,
      pageContext,
    );

    const storedEntry = getPageStarterSessionEntries()[pageStarterSessionKey];
    if (storedEntry?.content) {
      resolvedPageContextKey.current = pageStarterSessionKey;
      return applyPageStarterContent(sourceBotData, storedEntry.content);
    }

    try {
      const response = await axiosPost(
        "/liveBot/starter-content",
        {
          botId: sourceBotData._id,
          embed: true,
          pageContext,
        },
        { timeout: STARTER_REQUEST_TIMEOUT_MS },
      );
      const content = response?.data;
      if (!content?.success) return sourceBotData;
      const starterContent = {
        initialMessage: content.initialMessage,
        questions: content.questions,
      };
      resolvedPageContextKey.current = pageStarterSessionKey;
      // Only final content is kept for the tab; saved content returned while AI is
      // still writing (or after an error) must not stick for the rest of the session.
      if (CACHEABLE_STARTER_STATUSES.includes(content.cacheStatus)) {
        savePageStarterSessionEntry(pageStarterSessionKey, starterContent);
      } else if (content.cacheStatus === "pending") {
        setStarterRetry((current) =>
          current?.key === pageStarterSessionKey
            ? { key: pageStarterSessionKey, attempt: current.attempt + 1 }
            : { key: pageStarterSessionKey, attempt: 0 },
        );
      }

      return applyPageStarterContent(sourceBotData, starterContent);
    } catch (error) {
      console.warn("Unable to resolve page-aware starter content", error);
      return sourceBotData;
    }
  }

  async function refreshPageAwareStarterContent(
    payload: Record<string, any>,
    { retry = false }: { retry?: boolean } = {},
  ) {
    const sourceBotData = baseBotData.current;
    if (
      !sourceBotData ||
      resolvingStarterContent.current ||
      (!retry && !hasPageContextUpdate(payload)) ||
      roomId ||
      resumeConversationData ||
      recentConversations.length
    ) {
      return;
    }
    // Already showing this page's content.
    const pageContext = getPageContext(embedData.current);
    if (
      !retry &&
      pageContext.parent_url &&
      resolvedPageContextKey.current ===
        getPageStarterSessionKey(sourceBotData, pageContext)
    ) {
      return;
    }

    resolvingStarterContent.current = true;
    try {
      const runtimeBotData = await resolvePageAwareStarterContent(
        sourceBotData,
        embedData.current,
      );
      const sameStarterContent =
        runtimeBotData?.aiStarterQues?.initialMsg ===
          botData?.aiStarterQues?.initialMsg &&
        JSON.stringify(runtimeBotData?.aiStarterQues?.data) ===
          JSON.stringify(botData?.aiStarterQues?.data);
      if (runtimeBotData !== botData && !sameStarterContent) {
        setBotData(runtimeBotData);
        setChatConfig(runtimeBotData.chatConfig);
        setShowPreview(true);
        reloadPreview();
      }
    } finally {
      resolvingStarterContent.current = false;
    }
  }

  async function initEmbed(
    botData: Record<string, any>,
    fromOutgrowValue = false,
  ) {
    const textColor = getTextColor(botData.chatConfig.botBubbleColor);

    window.parent.postMessage(
      {
        ...botData.chatConfig,
        embedConfig: botData?.embedConfig,
        close: false,
        dynamicColor: textColor,
        botCustomId: botData.botCustomId,
        // Draft and published bots share botCustomId; page-aware popups need the exact one.
        botId: botData._id,
        SERVER_API_URL: environmentData.SERVER_API_URL,
        fromOutgrow: fromOutgrowValue,
        showGemini:
          botData?.enableGemini && botData?.embedConfig?.showVoiceIcon,
        omniData: true,
      },
      "*",
    );
  }

  async function fetchContact(companyId: string) {
    try {
      let contactId = window.localStorage.getItem("cbb-contact-id");
      if (contactId === "undefined") {
        window.localStorage.removeItem("cbb-contact-id");
        contactId = "";
      }

      const userEmail = embedData.current.email ?? "";
      if (!contactId && !userEmail) return null;

      const res = await axiosPost("/contact", {
        companyId,
        contactId,
        userEmail,
      });

      setContactData(res.data);
      if (res.data) {
        setContactName(res.data.fieldData.name);
        window.localStorage.setItem("cbb-contact-id", res.data._id);
      }

      return res.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  async function fetchCredits(companyId: string) {
    try {
      const res = await axiosGet(`liveBot/credits?compId=${companyId}`);
      setCreditId(res.data.creditId);
      // setIsTrial(res.data.isTrial);
      setFromOutgrow(res.data.fromOutgrow);
      return !!res.data.fromOutgrow;
    } catch (err) {
      console.log(err);
      return false;
    }
  }

  async function fetchRecentConversations(
    contact: Record<string, any>,
    botId: string,
  ) {
    try {
      if (!contact) return [];
      setRecentConversationsLoder(true);

      const res = await axiosGet(
        `liveBot/recent-conversations?contactId=${contact._id}&botId=${botId}`,
      );
      setRecentConversations(res.data);
      setRecentConversationsLoder(false);

      return res.data;
    } catch (err) {
      console.error(err);
      setRecentConversationsLoder(false);

      return [];
    }
  }

  function processEmbedData() {
    const _embedData: Record<string, string> = {};

    const localEmbedDataRaw = window.localStorage.getItem(
      "cbb-contact-embed-data",
    );
    if (embed.current && localEmbedDataRaw) {
      try {
        const localEmbedData = JSON.parse(localEmbedDataRaw);
        Object.assign(_embedData, localEmbedData);
      } catch {}
    }

    for (const [key, value] of searchParams.entries()) {
      if (key === "debug" || key === "embed") continue;
      _embedData[key] = value;
    }

    embedData.current = _embedData;
    window.localStorage.setItem(
      "cbb-contact-embed-data",
      JSON.stringify(_embedData),
    );
  }

  async function fetchLocation() {
    const locationRes = await axiosGet("/livebot/getLocation");
    const { country, regionName, city, ip } = locationRes.data?.data;

    const locationData = {
      ip: ip?.ip_address,
      location: `${city}, ${regionName}, ${country}`,
      country: country?.toLowerCase() ?? "",
      region: regionName?.toLowerCase() ?? "",
      city: city?.toLowerCase() ?? "",
    };

    setLocationData(locationData);
  }

  async function resumeConversation(conversationId: string) {
    try {
      const res = await axiosGet(`/liveBot/conversation/${conversationId}`);

      setResumeConversationData(res.data.conversation);
      setShowPreview(true);
    } catch (err) {
      console.log(err);
    }
  }

  async function handleEmbedEvents(event: MessageEvent) {
    switch (event.data.type) {
      case "setUserData": {
        const payload = event.data.payload ?? {};
        embedData.current = {
          ...embedData.current,
          ...payload,
        };
        window.localStorage.setItem(
          "cbb-contact-embed-data",
          JSON.stringify(embedData.current),
        );
        await refreshPageAwareStarterContent(payload);
        break;
      }
      case "getUserData": {
        window.parent.postMessage(
          {
            type: "log",
            payload: embedData.current,
          },
          "*",
        );
        break;
      }
      case "resetChat": {
        window.localStorage.removeItem("cbb-contact-id");

        window.parent.postMessage(
          {
            type: "close",
          },
          "*",
        );

        const contact = await fetchContact(botData.companyId);

        if (contact) {
          await transferChat(contact, contactData);

          const recentConversation = await fetchRecentConversations(
            contact,
            botData._id,
          );
          setShowPreview(!recentConversation.length);

          reloadPreview();
        } else {
          const res = await axiosPost("/contact/create", {
            companyId: botData.companyId,
            botCustomId: botData.botCustomId,
            embedData: {
              //@ts-ignore
              ...(window.cbbSettings?.user_data ?? {}),
              ...embedData.current,
            },
          });

          setContactName(res.data.name);
          await transferChat(res.data, contactData);

          const recentConversation = await fetchRecentConversations(
            res.data,
            botData._id,
          );
          setShowPreview(!recentConversation.length);

          reloadPreview();

          setContactData(res.data);

          window.localStorage.setItem("cbb-contact-id", res.data._id ?? "");
        }
        break;
      }
      case "sendMessage": {
        if (event.data.payload) {
          setShowPreview(true);
          setTimeout(() => {
            if (event.data.payload.role === "assistant") {
              previewComponentRef.current?.sendAssistantMessage(
                event.data.payload.message,
              );
            }
            if (event.data.payload.role === "user") {
              previewComponentRef.current?.sendUserMessage(
                event.data.payload.message,
              );
            }
          }, 1000);
        }
        break;
      }
      case "sendStaticMessages": {
        if (
          event.data.payload?.messages &&
          Array.isArray(event.data.payload.messages)
        ) {
          setShowPreview(true);
          previewComponentRef.current?.sendStaticMessages(
            event.data.payload.messages,
          );
        }
        break;
      }
      case "showWelcomeScreen": {
        setShowPreview(false);
        break;
      }
      case "showConversationScreen": {
        setShowPreview(true);
        break;
      }
      case "createContact": {
        try {
          const response = await axiosPost("/contact/create", {
            companyId: botData.companyId,
            botCustomId: botData.botCustomId,
            embedData: {
              ...(embedData.current ?? {}),
              ...(event.data.payload ?? {}),
            },
          });
          window.localStorage.setItem(
            "cbb-contact-id",
            response.data.contact._id,
          );
        } catch (err) {
          console.log(err);
        }
        break;
      }
    }
  }

  async function transferChat(
    contact: Record<string, any>,
    prevContact: Record<string, any> | null,
  ) {
    try {
      if (!prevContact) return;
      if (prevContact.contactType === "user") return;
      if (!contact) return;

      const _roomId = roomId || recentConversations?.[0]?._id;
      if (!_roomId) {
        console.log("no room id found: ", _roomId);
        return;
      }

      await axiosPost("/liveBot/transfer-conversation", {
        contactId: contact._id,
        userResponseId: _roomId,
      });

      setRoomId(null);
    } catch (err) {
      console.error(err);
    }
  }

  if (loader) {
    if (outgrow) {
      return (
        <div className="pre-loader">
          <img
            src="https://dlvkyia8i4zmz.cloudfront.net/BfcoBqtbS7K8Koyso1TS_logo_animation.gif"
            alt="Outgrow Logo"
            height={50}
            width={50}
            className="outgrow-loader"
          />
        </div>
      );
    }

    const fallbackBg = "#f8f8f8";
    const fallbackHeaderBg = "#f0f0f0";
    const fallbackSkeleton = "#e0e0e0";
    const fallbackAccent = "#d0d0d0";

    if (!embed.current) {
      return (
        <div
          className="pre-loader cbb-pre-loader"
          style={{ background: fallbackBg }}
        >
          <div className="cbb-skeleton-loader flex h-full w-full flex-col">
            {/* Header */}
            <div
              className="cbb-skeleton-header flex w-full items-center gap-4 border-b border-white/5 px-6 py-5"
              style={{ background: fallbackHeaderBg }}
            >
              <div
                className="cbb-skeleton-avatar h-12 w-12 !flex-shrink-0 animate-pulse rounded-full shadow-sm"
                style={{
                  background: fallbackSkeleton,
                  animationDuration: "1.8s",
                  animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                }}
              />
              <div className="cbb-skeleton-header-content flex w-full flex-col !items-start gap-2.5">
                <div
                  className="cbb-skeleton-title h-4 w-36 animate-pulse rounded-md"
                  style={{
                    background: fallbackSkeleton,
                    animationDuration: "1.8s",
                    animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                    animationDelay: "0.1s",
                  }}
                />
                <div
                  className="cbb-skeleton-subtitle h-3 w-24 animate-pulse rounded-md opacity-60"
                  style={{
                    background: fallbackSkeleton,
                    animationDuration: "1.8s",
                    animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                    animationDelay: "0.2s",
                  }}
                />
              </div>
            </div>

            {/* Messages */}
            <div className="cbb-skeleton-messages !items-normal mx-auto w-full max-w-[945px] flex-1 flex-col !justify-start gap-6 p-6">
              {/* Incoming message 1 */}
              <div className="cbb-skeleton-msg-incoming animate-fade-in flex w-full !items-start gap-3">
                <div
                  className="cbb-skeleton-bot-avatar h-8 w-8 flex-shrink-0 animate-pulse rounded-full shadow-sm"
                  style={{
                    background: fallbackSkeleton,
                    animationDuration: "1.8s",
                    animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                  }}
                />
                <div className="cbb-skeleton-msg-content flex w-full flex-col !items-start gap-2.5">
                  <div
                    className="cbb-skeleton-msg-bubble h-11 w-52 animate-pulse rounded-2xl shadow-sm"
                    style={{
                      background: fallbackSkeleton,
                      animationDuration: "1.8s",
                      animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                      animationDelay: "0.3s",
                    }}
                  />
                  <div
                    className="cbb-skeleton-msg-bubble h-16 w-64 animate-pulse rounded-2xl shadow-sm"
                    style={{
                      background: fallbackSkeleton,
                      animationDuration: "1.8s",
                      animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                      animationDelay: "0.4s",
                    }}
                  />
                </div>
              </div>

              {/* Outgoing message */}
              <div className="cbb-skeleton-msg-outgoing my-6 flex !items-start !justify-end gap-3">
                <div
                  className="cbb-skeleton-msg-bubble h-10 w-40 animate-pulse rounded-2xl shadow-sm"
                  style={{
                    background: fallbackAccent,
                    animationDuration: "1.8s",
                    animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                    animationDelay: "0.5s",
                  }}
                />
              </div>

              {/* Incoming message 2 */}
              <div className="cbb-skeleton-msg-incoming flex !items-start !justify-start gap-3">
                <div
                  className="cbb-skeleton-bot-avatar h-8 w-8 flex-shrink-0 animate-pulse rounded-full shadow-sm"
                  style={{
                    background: fallbackSkeleton,
                    animationDuration: "1.8s",
                    animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                  }}
                />
                <div className="cbb-skeleton-msg-content flex flex-col gap-2.5">
                  <div
                    className="cbb-skeleton-msg-bubble h-14 w-56 animate-pulse rounded-2xl shadow-sm"
                    style={{
                      background: fallbackSkeleton,
                      animationDuration: "1.8s",
                      animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                      animationDelay: "0.6s",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Input */}
            <div className="cbb-skeleton-input mx-auto flex w-full max-w-[945px] items-center gap-3 border-t border-white/5 px-6 py-5">
              <div
                className="cbb-skeleton-input-field h-12 flex-1 animate-pulse rounded-full shadow-sm"
                style={{
                  background: fallbackSkeleton,
                  animationDuration: "1.8s",
                  animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                }}
              />
              <div
                className="cbb-skeleton-send-btn h-10 w-10 animate-pulse rounded-full shadow-sm"
                style={{
                  background: fallbackAccent,
                  animationDuration: "1.8s",
                  animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                  animationDelay: "0.1s",
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className="pre-loader cbb-pre-loader cbb-welcome-screen-wrapper !flex !h-full !flex-col !items-start !justify-between"
        style={{ background: fallbackBg }}
      >
        {/* Header */}
        <div className="cbb-skeleton-header flex w-full items-center !justify-start gap-4 px-6 py-5">
          <div
            className="cbb-skeleton-avatar h-14 w-14 !flex-shrink-0 animate-pulse rounded-full shadow-sm"
            style={{
              background: fallbackSkeleton,
              animationDuration: "1.8s",
              animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
            }}
          />
          <div className="cbb-skeleton-header-content flex flex-col !items-start gap-2.5">
            <div
              className="cbb-skeleton-title h-5 w-40 animate-pulse rounded-md"
              style={{
                background: fallbackSkeleton,
                animationDuration: "1.8s",
                animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                animationDelay: "0.1s",
              }}
            />
            <div
              className="cbb-skeleton-subtitle h-3.5 w-32 animate-pulse rounded-md opacity-70"
              style={{
                background: fallbackSkeleton,
                animationDuration: "1.8s",
                animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                animationDelay: "0.2s",
              }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="cbb-skeleton-content flex w-full flex-col items-center justify-center gap-6 p-6">
          <div
            className="cbb-skeleton-recent-chats flex max-h-[100%] w-full max-w-[380px] flex-col overflow-hidden rounded-2xl shadow-lg"
            style={{
              background: "#fff",
              border: "1px solid rgba(0, 0, 0, 0.06)",
            }}
          >
            {/* Card Header */}
            <div
              className="cbb-skeleton-card-header flex h-16 w-full items-center !justify-start border-b px-5 py-4"
              style={{ borderBottomColor: "rgba(0, 0, 0, 0.08)" }}
            >
              <div
                className="cbb-skeleton-title-bar h-4 w-36 animate-pulse rounded-md"
                style={{
                  background: fallbackSkeleton,
                  animationDuration: "1.8s",
                  animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                  animationDelay: "0.3s",
                }}
              />
            </div>

            {/* Chat Items */}
            <div className="cbb-skeleton-chat-items flex w-full flex-col gap-1 p-5">
              {/* Chat Item 1 */}
              <div
                className="cbb-skeleton-chat-item flex w-full items-center justify-between gap-3 border-b py-3"
                style={{ borderBottomColor: "rgba(0, 0, 0, 0.06)" }}
              >
                <div className="cbb-skeleton-chat-item-grid grid w-full grid-cols-[44px_1fr] items-center gap-3.5">
                  <div
                    className="cbb-skeleton-chat-avatar h-11 w-11 animate-pulse rounded-full shadow-sm"
                    style={{
                      background: fallbackSkeleton,
                      animationDuration: "1.8s",
                      animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                      animationDelay: "0.4s",
                    }}
                  />
                  <div className="cbb-skeleton-chat-item-content flex w-full flex-col !items-start gap-2">
                    <div
                      className="cbb-skeleton-chat-item-title h-3.5 w-[85%] animate-pulse rounded-md"
                      style={{
                        background: fallbackSkeleton,
                        animationDuration: "1.8s",
                        animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                        animationDelay: "0.5s",
                      }}
                    />
                    <div
                      className="cbb-skeleton-chat-item-subtitle h-3 w-[55%] animate-pulse rounded-md opacity-60"
                      style={{
                        background: fallbackSkeleton,
                        animationDuration: "1.8s",
                        animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                        animationDelay: "0.6s",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Chat Item 2 */}
              <div className="cbb-skeleton-chat-item flex w-full items-center justify-between gap-3 py-3">
                <div className="cbb-skeleton-chat-item-grid grid w-full grid-cols-[44px_1fr] items-center gap-3.5">
                  <div
                    className="cbb-skeleton-chat-avatar h-11 w-11 animate-pulse rounded-full shadow-sm"
                    style={{
                      background: fallbackSkeleton,
                      animationDuration: "1.8s",
                      animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                      animationDelay: "0.7s",
                    }}
                  />
                  <div className="cbb-skeleton-chat-item-content flex w-full flex-col !items-start gap-2">
                    <div
                      className="cbb-skeleton-chat-item-title h-3.5 w-[85%] animate-pulse rounded-md"
                      style={{
                        background: fallbackSkeleton,
                        animationDuration: "1.8s",
                        animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                        animationDelay: "0.8s",
                      }}
                    />
                    <div
                      className="cbb-skeleton-chat-item-subtitle h-3 w-[55%] animate-pulse rounded-md opacity-60"
                      style={{
                        background: fallbackSkeleton,
                        animationDuration: "1.8s",
                        animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                        animationDelay: "0.9s",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div
            className="cbb-skeleton-cta-btn mt-2 h-12 w-56 animate-pulse rounded-xl shadow-sm"
            style={{
              background: fallbackAccent,
              animationDuration: "1.8s",
              animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
              animationDelay: "1s",
            }}
          />
        </div>

        {/* Footer */}
        <div className="cbb-skeleton-footer flex w-full items-center justify-center px-6 py-4">
          {/* <div
            className="h-3.5 w-48 animate-pulse rounded-md opacity-70"
            style={{
              background: fallbackSkeleton,
              animationDuration: "1.8s",
              animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
            }}
          /> */}
        </div>
      </div>
    );
  }

  function extractEmojis(text = "") {
    const emojiRegex =
      /(\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}\p{Emoji_Modifier}|\p{Emoji}(?:\u200D\p{Emoji})+)/gu;

    const emojiParts = [...text.matchAll(emojiRegex)].map((match) => match[0]);
    const textWithoutEmojis = text.replace(emojiRegex, "").trim();
    const emojiString = emojiParts.join(" ");

    return { textWithoutEmojis, emojiString, emojiParts };
  }
  const headerText = chatConfig?.greetingConfig?.header?.text || "";
  const descriptionText = chatConfig?.greetingConfig?.description?.text || "";
  const welcomeText = chatConfig?.welcomePageTxt || "";
  const {
    textWithoutEmojis: welcomeTextWithoutEmojis,
    emojiString: welcomeEmojiString,
  } = extractEmojis(welcomeText);
  const {
    textWithoutEmojis: headerTextWithoutEmojis,
    emojiString: headerEmojiString,
  } = extractEmojis(headerText);

  const {
    textWithoutEmojis: descTextWithoutEmojis,
    emojiString: descEmojiString,
  } = extractEmojis(descriptionText);

  const isBlackTextBot =
    botData?.url === "omni001-290" ||
    botData?.url === "tarun-4" ||
    botData?.url === "tarun-25";

  if (showPreview) {
    return (
      <div className="cbb-fade-in h-full w-full">
        <PreviewCbb
          ref={previewComponentRef}
          key={previewReloadKey}
          botUrl={url}
          embed={embed.current}
          environment={"live"}
          botData={botData}
          isPreviewModeChat={isPreviewMode}
          consent={consent}
          setConsent={setConsent}
          setReadPdf={setReadPdf}
          setReadPdfText={setReadPdfText}
          onBack={() => {
            notifyParentPreviewTab("welcome");
            if (isPreviewMode && isEmbeddedPreview) return;

            setShowPreview(false);
            if (contactData) {
              fetchRecentConversations(contactData, botData._id);
            }
          }}
          locationData={locationData}
          embedData={embedData.current}
          contactData={contactData}
          setContactData={setContactData}
          resumeConversationData={resumeConversationData}
          roomId={roomId}
          setRoomId={setRoomId}
          showBackBtn={embed.current && (recentConversations.length || roomId)}
          creditId={creditId}
          variableData={locationData}
          fromOutgrow={outgrow || fromOutgrow}
          isTrial={isTrial}
        />
        {readPdf && (
          <ReadPdfModal
            readPdf={readPdf}
            setReadPdf={setReadPdf}
            readPdfText={readPdfText}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`cbb-welcome-screen-wrapper entry-preview-wrapper preview-container-main cbb-fade-in !grid !h-full ${COMPARE_MODE ? "cbb-compare-mode grid-rows-[1fr]" : "grid-rows-[97px_1fr_30px]"} ![justify-content:unset]`}
      style={{
        background: chatConfig?.welcomePageBgColor ?? "",
        fontFamily: chatConfig?.greetingConfig?.typography?.font?.fontFamily,
      }}
    >
      {!COMPARE_MODE && (
        <Fragment>
          <div
            className={`cbb-welcome-screen-header fx items-center justify-between gap-[10px] p-[20px] pe-[50px]`}
          >
            <div
              className={`cbb-welcome-screen-header-content fx gap-[10px] ${chatConfig?.greetingConfig?.header?.text ? "items-center" : ""}`}
            >
              {chatConfig?.logo ? (
                <img
                  src={chatConfig?.logo}
                  alt="boticon"
                  className="cbb-welcome-screen-logo !h-[53px] !w-[53px] !rounded-[100px]"
                  onError={(e) =>
                    applyImageFallback(
                      e.currentTarget,
                      chatConfig?.logoFallbackUrl,
                    )
                  }
                />
              ) : outgrow ? (
                <OutgrowBotIcon />
              ) : (
                <BotIconOld />
              )}
              <div className="cbb-welcome-screen-headings-wrapper grid">
                <div className="cbb-welcome-screen-heading inline items-center">
                  <h2
                    className="cbb-welcome-screen-heading-content inline text-[22px] !font-semibold text-[#fff]"
                    style={{
                      color: isBlackTextBot
                        ? "#000"
                        : chatConfig?.greetingConfig?.header?.color,

                      fontSize:
                        chatConfig?.greetingConfig?.header?.fontSize + "px",
                      background: isBlackTextBot
                        ? "linear-gradient(90deg, rgb(0 0 0) 0%, rgb(0 0 0) 100%) text"
                        : `text ${chatConfig?.greetingConfig?.header?.color}`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {headerTextWithoutEmojis}
                  </h2>
                  {headerEmojiString && (
                    <span className="cbb-welcome-screen-heading-emoji emoji inline text-[22px]">
                      {headerEmojiString}
                    </span>
                  )}
                </div>
                {chatConfig?.greetingConfig?.description?.enabled && (
                  <div className="cbb-welcome-screen-description inline-flex items-center text-[14px] text-[#fff]">
                    <p
                      className="cbb-welcome-screen-description-content m-0 font-normal"
                      style={{
                        color: isBlackTextBot
                          ? "#000"
                          : chatConfig?.greetingConfig?.description?.color,

                        fontSize:
                          chatConfig?.greetingConfig?.description?.fontSize +
                          "px",
                        background: `text ${chatConfig?.greetingConfig?.description?.color}`,
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {descTextWithoutEmojis}
                    </p>
                    {descEmojiString && (
                      <span
                        className="cbb-welcome-screen-description-emoji emoji-desc ms-1 inline"
                        style={{
                          fontSize:
                            chatConfig?.greetingConfig?.description?.fontSize +
                            "px",
                        }}
                      >
                        {descEmojiString}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {(isMobileParent ||
            botData?.companyId === "64652848be7f202bf2716ffe") &&
            !pageLocation().href.includes("preview") && (
              <button
                className="cbb-mobile-screen-cross-icon absolute end-4 top-4 grid h-[20px] w-[20px] place-items-center rounded-full"
                style={{
                  background: deriveShade(chatConfig?.headerFooterBgColor),
                }}
                onClick={() => {
                  window.parent.postMessage({ type: "close" }, "*");
                }}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.8323 10.0166L19.62 2.22871C20.1268 1.72214 20.1268 0.90305 19.62 0.396534C19.1134 -0.110043 18.2943 -0.110043 17.7878 0.396534L9.99991 8.18433L2.21229 0.396534C1.70547 -0.110043 0.886691 -0.110043 0.380115 0.396534C-0.126705 0.903111 -0.126705 1.72214 0.380115 2.22871L8.16767 10.0166L0.380175 17.8044C-0.126644 18.3109 -0.126644 19.13 0.380175 19.6365C0.500338 19.7571 0.643127 19.8526 0.80034 19.9178C0.957552 19.9829 1.12609 20.0164 1.29627 20.0162C1.62791 20.0162 1.95967 19.8892 2.21236 19.6365L9.99991 11.8488L17.7878 19.6365C17.908 19.757 18.0508 19.8526 18.208 19.9177C18.3652 19.9829 18.5337 20.0163 18.7039 20.0162C19.0356 20.0162 19.3673 19.8892 19.62 19.6365C20.1268 19.13 20.1268 18.3109 19.62 17.8044L11.8323 10.0166Z"
                    fill={getTextColor(
                      deriveShade(chatConfig?.headerFooterBgColor),
                    )}
                  />
                </svg>
              </button>
            )}
        </Fragment>
      )}

      {recentConversationsLoader ? (
        <div className="cbb-welcome-screen-content previewContainer !m-[0] grid !min-h-[calc(100vh_-_132px)] place-items-center content-between !items-center gap-[30px] !pt-[0px]">
          <div
            className="cbb-skeleton-recent-chats mb-[20px] flex max-h-[100%] w-full max-w-[380px] flex-col overflow-hidden rounded-2xl shadow-lg"
            style={{
              background: "#fff",
              border: "1px solid rgba(0, 0, 0, 0.06)",
            }}
          >
            <div
              className="cbb-skeleton-card-header flex h-16 w-full items-center !justify-start border-b px-5 py-4"
              style={{ borderBottomColor: "rgba(0, 0, 0, 0.08)" }}
            >
              <div
                className="cbb-skeleton-title-bar h-4 w-36 animate-pulse rounded-md"
                style={{
                  background: "#e0e0e0",
                  animationDuration: "1.8s",
                  animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                  animationDelay: "0.3s",
                }}
              />
            </div>

            <div className="cbb-skeleton-chat-items flex w-full flex-col gap-1 p-5">
              <div
                className="cbb-skeleton-chat-item flex w-full items-center justify-between gap-3 border-b py-3"
                style={{ borderBottomColor: "rgba(0, 0, 0, 0.06)" }}
              >
                <div className="cbb-skeleton-chat-item-grid grid w-full grid-cols-[44px_1fr] items-center gap-3.5">
                  <div
                    className="cbb-skeleton-chat-avatar h-11 w-11 animate-pulse rounded-full shadow-sm"
                    style={{
                      background: "#e0e0e0",
                      animationDuration: "1.8s",
                      animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                      animationDelay: "0.4s",
                    }}
                  />
                  <div className="cbb-skeleton-chat-item-content flex w-full flex-col !items-start gap-2">
                    <div
                      className="cbb-skeleton-chat-item-title h-3.5 w-[85%] animate-pulse rounded-md"
                      style={{
                        background: "#e0e0e0",
                        animationDuration: "1.8s",
                        animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                        animationDelay: "0.5s",
                      }}
                    />
                    <div
                      className="cbb-skeleton-chat-item-subtitle h-3 w-[55%] animate-pulse rounded-md opacity-60"
                      style={{
                        background: "#e0e0e0",
                        animationDuration: "1.8s",
                        animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                        animationDelay: "0.6s",
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="cbb-skeleton-chat-item flex w-full items-center justify-between gap-3 py-3">
                <div className="cbb-skeleton-chat-item-grid grid w-full grid-cols-[44px_1fr] items-center gap-3.5">
                  <div
                    className="cbb-skeleton-chat-avatar h-11 w-11 animate-pulse rounded-full shadow-sm"
                    style={{
                      background: "#e0e0e0",
                      animationDuration: "1.8s",
                      animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                      animationDelay: "0.7s",
                    }}
                  />
                  <div className="cbb-skeleton-chat-item-content flex w-full flex-col !items-start gap-2">
                    <div
                      className="cbb-skeleton-chat-item-title h-3.5 w-[85%] animate-pulse rounded-md"
                      style={{
                        background: "#e0e0e0",
                        animationDuration: "1.8s",
                        animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                        animationDelay: "0.8s",
                      }}
                    />
                    <div
                      className="cbb-skeleton-chat-item-subtitle h-3 w-[55%] animate-pulse rounded-md opacity-60"
                      style={{
                        background: "#e0e0e0",
                        animationDuration: "1.8s",
                        animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
                        animationDelay: "0.9s",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <Fragment>
          <div className="cbb-welcome-screen-content previewContainer cbb-fade-in !min-h-[calc(100vh - 132px)] !m-[0] grid place-items-center content-between !items-center gap-[30px] !pt-[0px]">
            {isPreviewMode ? (
              <RecentMessages
                chatConfig={botData?.chatConfig}
                language={botData?.aiBotConfig?.language}
                conversations={defaultConversations}
                contactName={contactName}
                resumeConversation={resumeConversation}
              />
            ) : (
              <>
                {recentConversations.length ? (
                  <RecentMessages
                    chatConfig={botData.chatConfig}
                    language={botData?.aiBotConfig?.language}
                    conversations={recentConversations}
                    contactName={contactName}
                    resumeConversation={resumeConversation}
                  />
                ) : (
                  <div className="cbb-no-recent-chats-wrapper relative flex flex-col gap-3">
                    <NoConverstionSvg
                      fill={getTextColor(chatConfig?.welcomePageBgColor)}
                    />
                    <span
                      className={`cbb-no-recent-chats-text text-[14px] text-[#444444]`}
                      style={{
                        color: isBlackTextBot
                          ? "#000"
                          : getTextColor(chatConfig?.welcomePageBgColor),
                      }}
                    >
                      No Conversation yet
                    </span>
                  </div>
                )}
              </>
            )}
            <div className="cbb-start-chat-button-wrapper fx fx h-[100%] w-[100%] items-end">
              <button
                className="cbb-start-chat-button mx-auto mb-[30px] flex h-[44px] w-max min-w-[240px] max-w-[300px] justify-center gap-[10px] rounded-[10px] bg-gradient-to-r from-[#E3232C] via-[#FF787E] to-[#FF787E] px-[10px] py-[14px] text-[14px] font-medium capitalize text-[#FFF]"
                onClick={() => {
                  setRoomId(null);
                  setShowPreview(true);
                  setResumeConversationData(null);
                  notifyParentPreviewTab("conversation");
                }}
                style={{
                  background: chatConfig?.welcomePageBtnColor ?? "",
                  color: `${isBlackTextBot ? "black" : chatConfig?.welcomePageBtnColor}`,
                }}
              >
                <SendArrowIcon
                  useConditionalColors={true}
                  botData={botData}
                  gradientProps={
                    isBlackTextBot
                      ? { startColor: "black", endColor: "black" }
                      : parseGradient(chatConfig?.welcomePageBtnTxtColor)
                  }
                />
                <div className="cbb-start-chat-button-text-wrapper flex truncate">
                  <span
                    className="cbb-start-chat-button-text inline-block w-full truncate !bg-clip-text font-medium"
                    style={{
                      color: isBlackTextBot
                        ? "black"
                        : chatConfig?.welcomePageBtnTxtColor,
                      background: isBlackTextBot
                        ? "linear-gradient(90deg, rgb(0 0 0) 0%, rgb(0 0 0) 100%) text"
                        : `text ${chatConfig?.welcomePageBtnTxtColor}`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",

                      fontSize:
                        parseInt(chatConfig?.greetingConfig?.typography?.size) +
                        1 +
                        "px",
                    }}
                  >
                    {welcomeTextWithoutEmojis}
                  </span>
                  {welcomeEmojiString && (
                    <span className="cbb-start-chat-button-emoji inline text-[14px]">
                      {welcomeEmojiString}
                    </span>
                  )}
                </div>
              </button>
            </div>
          </div>
          {/* <div
            className={`footer-sec fx h-[100%] items-end w-[100%] ` }
            style={{display: "flex"}}
          > */}
          {!COMPARE_MODE && (
            <EntryBranding
              chatConfig={chatConfig}
              environmentData={environmentData}
              fromOutgrow={outgrow}
            />
          )}
          {/* </div> */}
        </Fragment>
      )}
    </div>
  );
}

const checkExistingConsent = () => {
  try {
    // Check localStorage first
    const localStorageConsent = window.localStorage?.getItem("privacy_policy");
    if (localStorageConsent === "true" || localStorageConsent === "false") {
      return false; // Don't show consent popup
    }

    // Check cookies as fallback
    const cookieConsent = document.cookie
      .split("; ")
      .find((row) => row.startsWith("privacy_policy="))
      ?.split("=")[1];

    if (cookieConsent === "true" || cookieConsent === "false") {
      return false; // Don't show consent popup
    }

    return true; // Show consent popup
  } catch (error) {
    console.error("Error checking existing consent:", error);
    return true; // Show consent popup if there's an error
  }
};

const BrandingLogoCBB = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.1026 31.4994C9.91362 31.4994 9.66169 31.4364 9.47274 31.3734C8.84291 31.1215 8.40203 30.4916 8.40203 29.7988V26.2718C5.6308 25.8939 2.92255 24.0674 1.41097 21.3592C-0.982368 17.1394 -0.289558 11.7858 3.17448 6.62129C5.94572 2.59041 10.4805 0.197074 15.645 0.134091C20.7466 0.0711089 25.5963 2.33848 28.3675 6.05444C31.0758 9.01462 33.5321 15.5648 30.8238 20.9813C28.7454 25.0752 23.5808 26.9016 15.897 26.3348L11.4252 30.9955C10.9843 31.3104 10.5434 31.4994 10.1026 31.4994ZM9.66168 23.3116C10.1026 23.3116 10.5434 23.5006 10.8584 23.7525C11.1733 24.0674 11.4252 24.5083 11.4252 25.0122V26.5867L14.0705 23.8785C14.4484 23.4376 15.0152 23.2487 15.645 23.3116C20.3057 23.6895 26.2261 23.3746 28.1786 19.5957C30.257 15.4388 28.2415 10.2113 26.1631 8.0069L26.0371 7.88094C23.8957 4.92076 19.9908 3.09427 15.771 3.15725C11.6141 3.22023 7.96116 5.10971 5.75677 8.32181C1.85185 14.0532 3.04852 18.0211 4.11922 19.9106C5.37887 22.115 7.58326 23.5006 9.72467 23.3746C9.5987 23.3116 9.66168 23.3116 9.66168 23.3116Z"
      fill="url(#paint0_linear_3814_44170)"
    />
    <path
      d="M17.0932 21.6127C16.9042 21.6127 16.7782 21.6127 16.5893 21.6127H15.5186C14.196 21.6127 12.9993 21.6127 11.8656 21.5497C10.417 21.4867 9.28332 21.2978 8.40156 20.9829C7.33086 20.605 6.57507 20.0381 6.07121 19.2824C5.50437 18.4006 5.18945 17.2669 5.18945 15.8183C5.18945 9.77201 10.0391 4.85938 16.0225 4.85938C19.1716 4.85938 22.1318 6.11902 24.2102 8.32341C26.0997 10.3389 27.0444 13.0471 26.8555 15.8183C26.8555 17.2669 26.5405 18.4006 25.9737 19.2824C25.4698 20.0381 24.714 20.605 23.6433 20.9829C22.7616 21.2978 21.6279 21.4867 20.1793 21.5497C19.2346 21.6127 18.1639 21.6127 17.0932 21.6127ZM16.0225 7.44166C11.4877 7.44166 7.77174 11.2206 7.77174 15.8183C7.77174 16.7001 7.89771 17.3929 8.21262 17.8338C8.33858 18.0227 8.59051 18.3376 9.28332 18.5266C9.91315 18.7785 10.7949 18.9045 11.9916 18.9674C13.0623 19.0304 14.2589 19.0304 15.5186 19.0304H16.3374H16.5893C17.8489 19.0304 19.0456 19.0304 20.1163 18.9674C21.313 18.9045 22.1947 18.7785 22.8246 18.5266C23.5174 18.2746 23.7063 18.0227 23.8953 17.8338C24.1472 17.3929 24.3362 16.7001 24.3362 15.8183V15.6294C24.4621 13.551 23.7693 11.5985 22.3837 10.0869C20.7461 8.38639 18.4158 7.44166 16.0225 7.44166Z"
      fill="#444444"
    />
    <path
      d="M12.0551 15.3757C12.7855 15.3757 13.3777 14.7836 13.3777 14.0531C13.3777 13.3226 12.7855 12.7305 12.0551 12.7305C11.3246 12.7305 10.7324 13.3226 10.7324 14.0531C10.7324 14.7836 11.3246 15.3757 12.0551 15.3757Z"
      fill="#FB5F66"
    />
    <path
      d="M15.9594 15.3757C16.6898 15.3757 17.282 14.7836 17.282 14.0531C17.282 13.3226 16.6898 12.7305 15.9594 12.7305C15.2289 12.7305 14.6367 13.3226 14.6367 14.0531C14.6367 14.7836 15.2289 15.3757 15.9594 15.3757Z"
      fill="#FB5F66"
    />
    <path
      d="M19.8656 15.3757C20.5961 15.3757 21.1882 14.7836 21.1882 14.0531C21.1882 13.3226 20.5961 12.7305 19.8656 12.7305C19.1351 12.7305 18.543 13.3226 18.543 14.0531C18.543 14.7836 19.1351 15.3757 19.8656 15.3757Z"
      fill="#FB5F66"
    />
    <defs>
      <linearGradient
        id="paint0_linear_3814_44170"
        x1="10.7523"
        y1="0.549272"
        x2="20.4962"
        y2="28.6162"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FF898E" />
        <stop offset="0.5469" stopColor="#EA0D17" />
        <stop offset="1" stopColor="#FB5F66" />
      </linearGradient>
    </defs>
  </svg>
);

interface SendArrowIconProps {
  width?: number;
  height?: number;
  gradientType?: "linear" | "radial";
  gradientAngle?: number;
  startColor?: string;
  endColor?: string;
  startOpacity?: number;
  endOpacity?: number;
  startOffset?: string;
  endOffset?: string;
  cx?: string;
  cy?: string;
  r?: string;
  fx?: string;
  fy?: string;
  backgroundColor?: string;
  stops?: { color: string; opacity: number; position: number }[] | null;
  fill?: string | null;
  botData?: { url?: string } | null;
  useConditionalColors?: boolean;
  conditionalBlackBots?: string[];
  blackColor?: string;
  whiteColor?: string;
  gradientProps?: {
    gradientType?: string;
    gradientAngle?: number;
    startColor?: string;
    endColor?: string;
    startOpacity?: number;
    endOpacity?: number;
    stops?: { color: string; opacity: number; position: number }[];
  };
  [key: string]: any;
}

export const SendArrowIcon = ({
  width = 17,
  height = 16,
  gradientType = "linear",
  gradientAngle = 45,
  startColor = "#FFFFFF",
  endColor = "#FFFFFF",
  startOpacity = 1,
  endOpacity = 1,
  startOffset = "0%",
  endOffset = "100%",
  // For radial gradient
  cx = "50%",
  cy = "50%",
  r = "50%",
  fx = "50%",
  fy = "50%",
  backgroundColor = "#FAFAFA",
  // For supporting parsed gradient results
  stops = null,
  // Solid color fallback
  fill = null,
  // Dynamic color conditions
  botData = null,
  useConditionalColors = false,
  conditionalBlackBots = ["omni001-290", "tarun-4", "tarun-25"],
  blackColor = "#000000",
  whiteColor = "#FFFFFF",
  // NEW: Accept gradientProps
  gradientProps = {},
  ...props
}: SendArrowIconProps) => {
  // Generate unique IDs for the gradient to avoid conflicts
  const gradientId = `gradientSendArrow_${Math.random().toString(36).substr(2, 9)}`;
  const clipPathId = `clipSendArrow_${Math.random().toString(36).substr(2, 9)}`;

  // Merge gradientProps with individual props (gradientProps takes precedence)
  const finalGradientType = gradientProps?.gradientType || gradientType;
  const finalGradientAngle = gradientProps?.gradientAngle || gradientAngle;
  const finalStartColor = gradientProps?.startColor || startColor;
  const finalEndColor = gradientProps?.endColor || endColor;
  const finalStartOpacity = gradientProps?.startOpacity || startOpacity;
  const finalEndOpacity = gradientProps?.endOpacity || endOpacity;
  const finalStops = gradientProps?.stops || stops;

  // Calculate gradient coordinates based on angle (for linear gradient)
  const angle = finalGradientAngle * (Math.PI / 180);
  const x1 = 50 + 50 * Math.cos(angle + Math.PI);
  const y1 = 50 + 50 * Math.sin(angle + Math.PI);
  const x2 = 50 + 50 * Math.cos(angle);
  const y2 = 50 + 50 * Math.sin(angle);

  // Determine fill color with proper priority handling
  let finalFill;

  // PRIORITY 1: Check for conditional colors (highest priority for specific bots)
  if (
    useConditionalColors &&
    botData?.url &&
    conditionalBlackBots.includes(botData.url)
  ) {
    finalFill = blackColor;
  }
  // PRIORITY 2: Check for direct fill color
  else if (fill && fill !== "transparent" && fill !== "") {
    finalFill = fill;
  }
  // PRIORITY 3: Check if we have gradient data (from gradientProps or stops)
  else if (finalStops && finalStops.length > 0) {
    finalFill = `url(#${gradientId})`;
  }
  // PRIORITY 4: Check if we have gradient colors
  else if (
    finalStartColor &&
    finalEndColor &&
    (finalStartColor !== finalEndColor || finalStartOpacity !== finalEndOpacity)
  ) {
    finalFill = `url(#${gradientId})`;
  }
  // PRIORITY 5: Use conditional white color for non-black bots
  else if (useConditionalColors && botData?.url) {
    finalFill = whiteColor;
  }
  // PRIORITY 6: Fallback to solid color
  else {
    finalFill = finalStartColor || whiteColor;
  }

  // Format stops for SVG - handle both gradientProps stops and legacy stops
  let gradientStops: { color: string; opacity: number; position: number }[] =
    [];

  if (finalStops && finalStops.length > 0) {
    gradientStops = finalStops;
  } else {
    // Create stops from start/end colors
    gradientStops = [
      { color: finalStartColor, opacity: finalStartOpacity, position: 0 },
      { color: finalEndColor, opacity: finalEndOpacity, position: 100 },
    ];
  }

  // Only create gradient if we're actually using it
  const shouldCreateGradient = finalFill.includes(`url(#${gradientId})`);

  return (
    <svg
      className="cbb-start-chat-button-icon flex-shrink-0"
      width={width}
      height={height}
      viewBox="0 0 17 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {shouldCreateGradient && (
        <defs>
          {finalGradientType === "linear" ? (
            <linearGradient
              id={gradientId}
              x1={`${x1}%`}
              y1={`${y1}%`}
              x2={`${x2}%`}
              y2={`${y2}%`}
            >
              {gradientStops.map((stop, index) => (
                <stop
                  key={index}
                  offset={`${stop.position}%`}
                  stopColor={stop.color}
                  stopOpacity={stop.opacity}
                />
              ))}
            </linearGradient>
          ) : (
            <radialGradient
              id={gradientId}
              cx={cx}
              cy={cy}
              r={r}
              fx={fx}
              fy={fy}
            >
              {gradientStops.map((stop, index) => (
                <stop
                  key={index}
                  offset={`${stop.position}%`}
                  stopColor={stop.color}
                  stopOpacity={stop.opacity}
                />
              ))}
            </radialGradient>
          )}
          <clipPath id={clipPathId}>
            <rect width={width} height={height} fill="white" />
          </clipPath>
        </defs>
      )}
      <g clipPath={shouldCreateGradient ? `url(#${clipPathId})` : undefined}>
        <path
          d="M0.903566 15.2698C0.772093 15.874 1.23595 16.1297 1.66635 15.9367L16.2129 8.47783H16.2143C16.3878 8.37996 16.4886 8.20361 16.4886 8.00017C16.4886 7.79662 16.3878 7.62014 16.2143 7.52232H16.2129L1.66635 0.0632796C1.23595 -0.12972 0.772093 0.12611 0.903566 0.730296C0.912463 0.77122 1.77244 4.60191 2.24001 6.68528L9.83353 8.00013L2.24001 9.31483C1.77244 11.3981 0.912423 15.2288 0.903566 15.2698Z"
          fill={finalFill}
        />
      </g>
    </svg>
  );
};
